import {
  Box,
  Heading,
  HStack,
  Icon,
  Image,
  Text,
  VStack
} from "@chakra-ui/react";
import { ChevronRight, CircleDot, Network, Sparkles } from "lucide-react";

import type { GameShowcaseMetadata } from "@/data/games";
import { useI18n } from "@/i18n";
import { GlowButton } from "@/shared/ui/GlowButton";
import { NvpCard } from "@/shared/ui/NvpCard";
import { PrimitiveBadge } from "@/shared/ui/PrimitiveBadge";

interface GameCardProps {
  readonly game: GameShowcaseMetadata;
}

const visualIcons = {
  "witch-broom": Sparkles,
  "bridges": Network,
  "dice-triangles": CircleDot
} as const;

export function GameCard({ game }: GameCardProps) {
  const { copy, localize } = useI18n();
  const isLive = game.status === "live";

  return (
    <NvpCard
      role="article"
      interactive
      tone="pink"
      minH={{ base: "360px", md: "360px" }}
      h="100%"
      bg="linear-gradient(180deg, rgba(27,22,58,0.98) 0%, rgba(27,9,45,0.96) 46%, rgba(31,18,37,0.98) 100%)"
      borderColor={game.status === "live" ? "rgba(255,79,216,0.72)" : "rgba(55,213,255,0.42)"}
    >
      <Box position="absolute" inset={0} bg={game.gradient} opacity={0.28} />
      <Box
        position="absolute"
        inset="-1px"
        bg="radial-gradient(circle at 30% 8%, rgba(255,255,255,0.28), transparent 34%), radial-gradient(circle at 84% 78%, rgba(255,79,216,0.24), transparent 34%), linear-gradient(180deg, transparent 0%, rgba(255,216,77,0.08) 100%)"
      />
      <VStack align="start" h="100%" p={4} spacing={4}>
        <HStack justify="space-between" w="100%" position="relative" zIndex={1}>
          <PrimitiveBadge tone="pink">{localize(game.genre)}</PrimitiveBadge>
          <Sparkles color={game.accentColor} size={20} />
        </HStack>

        <GameVisual game={game} />

        <VStack align="start" position="relative" zIndex={1} w="100%" mt="auto" spacing={4}>
          <Box w="100%">
            <Heading
              as="h3"
              fontSize={{ base: "2xl", md: "2xl" }}
              mb={2}
              noOfLines={1}
              textShadow="0 0 18px rgba(255,255,255,0.18)"
            >
              {localize(game.title)}
            </Heading>
            <Text color="whiteAlpha.800" fontSize="md" noOfLines={2}>
              {localize(game.tagline)}
            </Text>
          </Box>

          {isLive ? (
            <GlowButton
              as="a"
              href={`/games/${game.slug}`}
              glow="blue"
              variantKind="secondary"
              motion="lift"
              rightIcon={<ChevronRight size={16} />}
              minH="44px"
              px={5}
              size="sm"
            >
              {copy.games.learnMore}
            </GlowButton>
          ) : (
            <PrimitiveBadge tone="blue">{copy.games.comingSoon}</PrimitiveBadge>
          )}
        </VStack>
      </VStack>
    </NvpCard>
  );
}

function GameVisual({ game }: GameCardProps) {
  if (game.image) {
    return (
      <Box
        position="relative"
        zIndex={1}
        w="100%"
        h="138px"
        overflow="hidden"
        borderRadius="8px"
        border="1px solid"
        borderColor="rgba(255,79,216,0.46)"
        bg={game.gradient}
        boxShadow="0 18px 44px rgba(0,0,0,0.38), 0 0 24px rgba(255,79,216,0.2)"
      >
        <Image
          src={game.image}
          alt={game.title.en}
          h="100%"
          w="100%"
          objectFit={game.imageFit ?? "cover"}
          objectPosition="center"
        />
      </Box>
    );
  }

  const nodes = [
    ["18%", "24%"],
    ["38%", "48%"],
    ["62%", "30%"],
    ["78%", "62%"],
    ["28%", "76%"]
  ];

  return (
    <Box
      position="relative"
      zIndex={1}
      w="100%"
      h="138px"
      overflow="hidden"
      borderRadius="8px"
      border="1px solid"
      borderColor="rgba(55,213,255,0.42)"
      bg="rgba(2, 4, 16, 0.72)"
      boxShadow="0 18px 44px rgba(0,0,0,0.38), 0 0 26px rgba(55,213,255,0.18)"
    >
      <Box position="absolute" inset={0} bg={game.gradient} opacity={0.2} />
      <Box
        position="absolute"
        inset={0}
        bg="linear-gradient(90deg, rgba(255,255,255,0.04) 1px, transparent 1px), linear-gradient(0deg, rgba(255,255,255,0.04) 1px, transparent 1px)"
        backgroundSize="28px 28px"
      />
      <Box
        position="absolute"
        left="15%"
        right="16%"
        top="36%"
        h="2px"
        bg={game.accentColor}
        opacity={0.72}
        transform="rotate(18deg)"
      />
      <Box
        position="absolute"
        left="26%"
        right="18%"
        top="58%"
        h="2px"
        bg="#37d5ff"
        opacity={0.62}
        transform="rotate(-24deg)"
      />
      <Box
        position="absolute"
        left="22%"
        right="27%"
        top="48%"
        h="2px"
        bg="#ffd84d"
        opacity={0.56}
        transform="rotate(58deg)"
      />
      {nodes.map(([left, top], index) => (
        <Box
          key={`${left}-${top}`}
          position="absolute"
          left={left}
          top={top}
          h={index === 1 ? "14px" : "10px"}
          w={index === 1 ? "14px" : "10px"}
          borderRadius="999px"
          bg="white"
          border="2px solid"
          borderColor={index % 2 === 0 ? game.accentColor : "#37d5ff"}
          boxShadow={`0 0 16px ${index % 2 === 0 ? game.accentColor : "#37d5ff"}`}
        />
      ))}
      <Box position="absolute" right="14px" bottom="12px" color="whiteAlpha.900">
        <Icon as={visualIcons[game.visual]} boxSize={8} />
      </Box>
    </Box>
  );
}
